import { api } from '../lib/api'

// Espelha src/services/panel.ts e src/services/dailyMetrics.ts.

export type PanelBlock = 'kpis' | 'serie_diaria' | 'semanas' | 'campanhas' | 'plataformas' | 'site' | 'mercado'
export type PanelMetric =
  | 'spend'
  | 'conversions'
  | 'cost_per_conversion'
  | 'revenue'
  | 'roas'
  | 'clicks'
  | 'ctr'
  | 'cpc'
  | 'cpm'
  | 'impressions'
export type CompareMode = 'prev' | 'yoy' | 'none'
export type PanelPlatform = 'google_ads' | 'meta_ads'
export type MetricSource = PanelPlatform | 'ga4'

export interface PanelLayout {
  blocks: Array<{ key: PanelBlock; enabled: boolean }>
}

export interface PanelParams {
  from: string
  to: string
  compare: CompareMode
  platform: '' | PanelPlatform
  campaigns: string[]
}

export interface Sums {
  spend: number
  impressions: number
  clicks: number
  conversions: number
  conversionValue: number
}

export type Metrics = Record<PanelMetric, number | null>

export interface PanelData {
  clientName: string
  params: PanelParams
  range: { from: string; to: string; days: number }
  compare: { mode: CompareMode; from: string; to: string; incomplete: boolean } | null
  consolidatingFrom: string
  sources: Array<{ source: string; loadedFrom: string | null; loadedUntil: string | null }>
  hasData: boolean
  // Receita rastreada de verdade (servidor): decide se Receita e ROAS aparecem.
  revenueTracked: boolean
  rangeIncomplete: boolean
  definitions: string[]
  layout: PanelLayout
  kpis: Array<{
    key: PanelMetric
    label: string
    value: number | null
    compareValue: number | null
    deltaPct: number | null
    trend: 'higher' | 'lower' | 'neutral'
    target: { value: number; betterWhen: 'higher' | 'lower'; met: boolean | null } | null
  }>
  daily: Array<{ day: string } & Sums>
  dailyCompare: Array<{ day: string } & Sums> | null
  weekly: Array<{ weekStart: string; days: number } & Sums>
  campaigns: Array<{ id: string; name: string; platform: PanelPlatform; current: Metrics; compare: Metrics | null }>
  campaignOptions: Array<{ id: string; name: string; platform: PanelPlatform; spend: number }>
  platforms: Array<{ platform: PanelPlatform; current: Metrics; compare: Metrics | null }>
  site: {
    channels: Array<{
      channel: string
      sessions: number
      keyEvents: number
      revenue: number
      compareSessions: number | null
      compareKeyEvents: number | null
    }>
    daily: Array<{ day: string; sessions: number; keyEvents: number }>
    totals: { sessions: number; keyEvents: number; compareSessions: number | null; compareKeyEvents: number | null }
    compareIncomplete: boolean
  } | null
}

export interface PanelMarket {
  niche: string
  week: string
  regions: Array<{
    geo: number
    name: string
    history: Array<{ month: string; value: number | null }>
    forecast: Array<{ month: string; p10: number; p50: number; p90: number }>
  }>
  dates: Array<{ name: string; date: string }>
}

export interface SourceStatus {
  source: MetricSource
  loadedFrom: string | null
  loadedUntil: string | null
  lastRunAt: string | null
  lastError: string | null
  complete: boolean
}

export interface ReportCheck {
  reportId: string
  generatedAt: string
  status: 'ok' | 'divergente' | 'sem_dados'
  windows: Array<{
    label: string
    from: string
    to: string
    platforms: Array<{
      platform: PanelPlatform
      covered: boolean
      metrics: Array<{ metric: 'spend' | 'clicks' | 'conversions'; report: number; daily: number; diffPct: number | null; ok: boolean }>
    }>
  }>
}

export interface PanelStatus {
  active: boolean
  historyStart: string
  historyLoading: boolean
  sources: SourceStatus[]
  check: ReportCheck | null
}

export interface DailyMetricsOverview {
  historyStart: string
  historyLoading: boolean
  clients: Array<{ id: string; name: string; sources: SourceStatus[] }>
}

function query(p: PanelParams): string {
  const q = new URLSearchParams({ from: p.from, to: p.to, compare: p.compare })
  if (p.platform) q.set('platform', p.platform)
  if (p.campaigns.length) q.set('campaigns', p.campaigns.join(','))
  return q.toString()
}

export const panelApi = {
  get: (clientId: string, p: PanelParams) => api.get<{ panel: PanelData }>(`/clients/${clientId}/panel?${query(p)}`),
  status: (clientId: string) => api.get<{ status: PanelStatus }>(`/clients/${clientId}/panel/status`),
  market: (clientId: string) => api.get<{ market: PanelMarket | null }>(`/clients/${clientId}/panel/market`),
  sync: (clientId: string) =>
    api.post<{ ok: boolean; rows: number; errors: string[]; message?: string }>(`/clients/${clientId}/panel/sync`),
  saveLayout: (clientId: string, layout: PanelLayout) =>
    api.put<{ layout: PanelLayout }>(`/clients/${clientId}/panel/layout`, layout),
  overview: () => api.get<DailyMetricsOverview>('/settings/daily-metrics'),
  loadHistory: () => api.post<{ started: boolean }>('/settings/daily-metrics/history'),
}
